import React from 'react';
import { useTranslation } from 'react-i18next';
import { FeedRegistrationsService } from './FeedRegistrationsService';
import { FeedRegistration, FeedRegistrationsQuery } from '../models/feed-registration';
import useAuthContext from './useAuthContext';
import { extractApiErrorMessage, notifications } from '../utils';

interface UseFeedRegistrationsResult {
  registrations: FeedRegistration[];
  totalCount: number;
  isBusy: boolean;
  /** True once a fetch has settled, successfully or not - lets callers tell "empty" from "not asked yet". */
  loaded: boolean;
  reload: () => void;
}

/**
 * Loads the manual feed registrations for a set of pens. The same data feeds the pen tab on the
 * location view and the room/row panel on the locations page, which differ only in how many pen ids
 * they pass in - so the fetch lives here rather than in either of them.
 *
 * An empty `locationIds` means "no scope picked yet", not "every pen": the effect skips the request
 * and reports an empty, settled result, so the caller can render its own empty state instead of
 * pulling the whole farm.
 */
export const useFeedRegistrations = (query: FeedRegistrationsQuery): UseFeedRegistrationsResult => {
  const { t } = useTranslation();
  const { state } = useAuthContext();
  const [registrations, setRegistrations] = React.useState<FeedRegistration[]>([]);
  const [totalCount, setTotalCount] = React.useState(0);
  const [isBusy, setIsBusy] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);
  const [reloadToken, setReloadToken] = React.useState(0);

  const { locationIds, startDate, endDate, page, size } = query;
  const { farmId } = state;
  // The array is a fresh reference on every render; the effect depends on its contents instead.
  const locationKey = locationIds.join(',');

  React.useEffect(() => {
    if (!farmId || locationIds.length === 0) {
      setRegistrations([]);
      setTotalCount(0);
      setLoaded(true);
      return;
    }

    const service = new FeedRegistrationsService();
    // The scope and the period can change while a request is in flight; ignore all but the newest.
    let cancelled = false;

    setIsBusy(true);

    service
      .getRegistrations(farmId, { locationIds, startDate, endDate, page, size })
      .then((result) => {
        if (cancelled) return;
        setRegistrations(result.items);
        setTotalCount(result.totalCount);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setRegistrations([]);
        setTotalCount(0);
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        if (cancelled) return;
        setIsBusy(false);
        setLoaded(true);
      });

    return () => {
      cancelled = true;
    };
    // Destructured rather than depending on `query`, which is a fresh object on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [farmId, locationKey, startDate, endDate, page, size, reloadToken]);

  const reload = React.useCallback(() => setReloadToken((token) => token + 1), []);

  return { registrations, totalCount, isBusy, loaded, reload };
};

export default useFeedRegistrations;
